import React from 'react';
import { motion } from 'framer-motion';
import { FaReact, FaFigma } from 'react-icons/fa';
import { SiFramer, SiTailwindcss, SiNextdotjs, SiTypescript, SiJavascript, SiVuedotjs } from 'react-icons/si';

const ProjectsSection = () => {
  return (
    <section className="py-12 my-16 bg-white">
      <div className="container mx-auto px-4 md:px-8 lg:px-16">
        <div className="mb-12">
          <motion.h2 
            className="text-2xl md:text-3xl lg:text-4xl font-medium mb-6 text-[#0a2540] leading-tight max-w-ld"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Des technologies modernes <br></br> pour des expériences exceptionnelles
          </motion.h2>
          
          <motion.p 
            className="text-gray-600 max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Chez aiko, nous utilisons les technologies les plus avancées pour créer des sites web et des applications 
            qui se démarquent. Notre stack technique moderne, combinée à notre expertise en design, nous permet de 
            développer des interfaces fluides, réactives et visuellement impressionnantes. Nous privilégions 
            <span className="text-indigo-600"> React.js</span>, <span className="text-indigo-600">Next.js</span> et 
            <span className="text-indigo-600"> Framer Motion</span> pour garantir des animations fluides et des 
            transitions élégantes qui captent l'attention de vos utilisateurs et inspirent confiance.
          </motion.p>
          
          <motion.div
            className="mt-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <button className="px-5 py-2 rounded-[40px] bg-[#5046E5] text-white font-medium hover:bg-[#4038c2] transition-all duration-300 shadow-lg text-sm inline-flex items-center">
              Découvrir nos services
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
          </motion.div>
        </div>
        
        <div className="flex flex-col md:flex-row">
          {/* Colonne de gauche avec les technologies */}
          <div className="md:w-1/3 pr-8">
            <div className="mb-12">
              <div className="border-l-4 border-indigo-600 pl-4 mb-2">
                <div className="text-3xl font-bold text-[#0a2540]">100%</div>
                <div className="text-gray-600 text-sm">de sites responsive</div>
              </div>
            </div>
            
            <div className="mb-12">
              <div className="border-l-4 border-indigo-600 pl-4 mb-2">
                <div className="text-3xl font-bold text-[#0a2540]">+40%</div>
                <div className="text-gray-600 text-sm">d'engagement utilisateur</div>
              </div>
            </div>
            
            <div className="mb-8">
              <div className="text-sm font-medium text-gray-700 mb-4">Technologies utilisées</div>
              <div className="grid grid-cols-2 gap-1">
                {/* Première colonne de technologies */}
                <div className="space-y-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-blue-500">
                    <FaReact size={28} />
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-black">
                    <SiNextdotjs size={28} />
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-purple-500">
                    <SiFramer size={28} />
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-blue-600">
                    <FaFigma size={28} />
                  </div>
                </div>
                
                {/* Deuxième colonne de technologies */}
                <div className="space-y-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-teal-500">
                    <SiTailwindcss size={28} />
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-blue-600">
                    <SiTypescript size={28} />
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-green-500">
                    <SiVuedotjs size={28} />
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-yellow-500">
                    <SiJavascript size={28} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Colonne de droite avec le cas client */}
          <div className="md:w-2/3">
            <motion.div 
              className="bg-gradient-to-r from-indigo-500 to-indigo-700 rounded-xl overflow-hidden relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="p-8 text-white relative z-10 flex flex-col h-full justify-between min-h-[450px]">
                <div className="flex justify-between items-start">
                  <div className="text-2xl font-bold bg-white text-indigo-600 px-3 py-1 rounded-md inline-block">
                    aiko
                  </div>
                  <div className="bg-white rounded-full p-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                </div>
                
                <div className="mt-auto">
                  <h3 className="text-2xl md:text-3xl font-bold mb-2">
                    Des interfaces modernes et des animations fluides qui transforment l'expérience utilisateur
                  </h3>
                  <p className="text-white/80 mt-4">
                    Notre expertise en développement front-end nous permet de créer des sites web qui se démarquent par leur design contemporain et leurs interactions fluides, offrant une expérience utilisateur exceptionnelle qui inspire confiance à vos clients.
                  </p>
                </div>
              </div>
              
              {/* Image overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/60 to-indigo-700/60 z-0"></div>
              <img 
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80" 
                alt="Développement web moderne" 
                className="absolute inset-0 w-full h-full object-cover mix-blend-overlay"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
